export const TWIKEY_PAYMENT_NAME = 'Twikey'
export const GPS_CCARD_TYPE = 'ccard'
export const BANK_TRANSFER_TYPE = 'BANKTRANSFER'
// Value of the `type` field on a saved SEPA payment record (upper-case).
// Not to be confused with the lower-case `SEPA` payment-type id in constant.js.
export const SEPA_RECORD_TYPE = 'SEPA'

const SEPA_PAYMENT_NAMES = new Set(['twikey', 'sepa'])

/**
 * Returns true for a SEPA direct debit identifier. GPS keys its options by a
 * content-managed label ('SEPA Direct Debit'), so this must be given the stable
 * option `id` ('SEPA') or a record name ('Twikey'), never the label.
 */
export const isSepaPaymentType = paymentType =>
  SEPA_PAYMENT_NAMES.has((paymentType || '').toString().trim().toLowerCase())

/**
 * Resolves the GPS options key for a payment type, matching the response key
 * first and then the option `id`, since the key is a content-managed label.
 */
export const findGpsOptionKey = (gpsOptions = {}, paymentType = '') => {
  const normalized = (paymentType || '').toString().trim().toLowerCase()
  if (!normalized) {
    return ''
  }
  const keys = Object.keys(gpsOptions || {})
  return (
    keys.find(key => key.toLowerCase() === normalized) ||
    keys.find(key =>
      (gpsOptions[key] || []).some(
        option =>
          (option?.id || '').toString().trim().toLowerCase() === normalized
      )
    ) ||
    ''
  )
}

/**
 * Returns true when a payment name refers to SEPA direct debit.
 * Records come back as either 'Twikey' or 'SEPA'
 */
export const isTwikeyPaymentName = paymentName => isSepaPaymentType(paymentName)

/**
 * Returns true only for the legacy record name 'Twikey' (case-insensitive).
 * Unlike isTwikeyPaymentName this does NOT match 'SEPA', which is also the
 * generic payment-type identifier used for a brand-new (unsaved) SEPA entry.
 */
export const isLegacyTwikeyPaymentName = paymentName =>
  (paymentName || '').toString().trim().toLowerCase() ===
  TWIKEY_PAYMENT_NAME.toLowerCase()

/**
 * Returns true when a saved payment record is a SEPA direct debit.
 * SEPA records are identified either by their record name ('Twikey' / 'SEPA')
 * or by their record type ('SEPA'), depending on which API produced them.
 *
 * The record name is passed explicitly because the field holding it differs
 * between payloads (for example the subscription payment modal exposes it as
 * `displayName`, while `name` there is the cardholder's first name).
 *
 * @param {Object} payment
 * @param {string} [payment.name] - record name ('Twikey' / 'SEPA')
 * @param {string} [payment.type] - record type ('SEPA')
 * @returns {boolean}
 */
export const isSepaPaymentRecord = payment => {
  const { name, type } = payment || {}
  return (
    isTwikeyPaymentName(name) ||
    (type || '').toString().trim().toUpperCase() === SEPA_RECORD_TYPE
  )
}

export const isSepaMandateInProgress = (payment = {}) => {
  const properties = payment?.properties || {}
  const hasMandateToken = Object.prototype.hasOwnProperty.call(
    properties,
    'subscriptionMandateToken'
  )

  if (!isTwikeyPaymentName(payment?.name || payment?.displayName)) {
    return false
  }

  if (!hasMandateToken) {
    return false
  }

  const mandateToken = (properties.subscriptionMandateToken || '')
    .toString()
    .trim()
    .toLowerCase()
  return mandateToken === 'na' || mandateToken === 'null' || mandateToken === ''
}

export const isExternalGPSCreditCardOption = paymentOption => {
  const processorURL = (paymentOption?.processorURL || '').toLowerCase()
  const paymentName = (paymentOption?.name || '').toLowerCase()

  return Boolean(
    processorURL &&
      !processorURL.includes('internal') &&
      !paymentName.includes('paymetric')
  )
}

const normalizePaymentName = paymentName => {
  return (paymentName || '').toString().trim().toLowerCase()
}

export const isPaymetricGPSCreditCardOption = paymentOption => {
  const paymentName = normalizePaymentName(paymentOption?.name)

  return paymentName.includes('paymetric')
}

const isConfiguredGPSCreditCardOption = paymentOption => {
  const processorURL = (paymentOption?.processorURL || '').toLowerCase()

  return Boolean(
    processorURL &&
      (!processorURL.includes('internal') ||
        isPaymetricGPSCreditCardOption(paymentOption))
  )
}

export const isExternalGPSCreditCardPaymentName = (
  paymentName,
  gpsCreditCardOptions = []
) => {
  const normalizedPaymentName = normalizePaymentName(paymentName)

  if (!normalizedPaymentName || !Array.isArray(gpsCreditCardOptions)) {
    return false
  }

  return gpsCreditCardOptions.some(option => {
    return (
      isExternalGPSCreditCardOption(option) &&
      normalizePaymentName(option?.name) === normalizedPaymentName
    )
  })
}

/**
 * Returns the GPS credit card option matching the configured processor name.
 * An unconfigured name returns undefined, so the caller stays on the non-GPS flow.
 */
export const getConfiguredExternalGPSCreditCardOption = (
  gpsCreditCardOptions = [],
  configuredProcessorName = ''
) => {
  const normalizedConfiguredName = normalizePaymentName(configuredProcessorName)

  if (!Array.isArray(gpsCreditCardOptions)) {
    return undefined
  }

  if (!normalizedConfiguredName) {
    return undefined
  }

  return gpsCreditCardOptions.find(
    option =>
      isExternalGPSCreditCardOption(option) &&
      normalizePaymentName(option?.name) === normalizedConfiguredName
  )
}

export const getConfiguredGPSCreditCardOption = (
  gpsCreditCardOptions = [],
  configuredProcessorName = ''
) => {
  const normalizedConfiguredName = normalizePaymentName(configuredProcessorName)

  if (!Array.isArray(gpsCreditCardOptions)) {
    return undefined
  }

  if (!normalizedConfiguredName) {
    return undefined
  }

  return gpsCreditCardOptions.find(
    option =>
      isConfiguredGPSCreditCardOption(option) &&
      normalizePaymentName(option?.name) === normalizedConfiguredName
  )
}

/**
 * Normalizes a GPS payment type string to the API-expected name.
 * GPS processors may return 'ccard' which must be sent as 'CREDITCARD'.
 */
export const resolveGpsPaymentName = (gpsType, paymentName = '') => {
  return (gpsType || '').toLowerCase() === GPS_CCARD_TYPE
    ? 'CREDITCARD'
    : paymentName
}

// BE expects "BANKTRANSFER" where GPS returns the option id "Wire".
const GPS_TYPE_OVERRIDES_BY_OPTION_ID = {
  wire: BANK_TRANSFER_TYPE,
}

const PAYMETRIC_PROCESS_CODE = 'paym'

/**
 * Returns true when a GPS credit card option is routed through Paymetric with
 * 3DS, i.e. its processCode is PAYM and its processorURL is present and not
 * internal. This is decided only from the option's own fields: GPS options no
 * longer carry a `name`, so the processor name must not be used.
 * @param {Object} paymentOption - a globalPaymentOptions CREDITCARD option
 * @returns {boolean}
 */
export const isPaymetric3DSProcessor = paymentOption => {
  const processCode = normalizePaymentName(paymentOption?.processCode)
  const processorURL = normalizePaymentName(paymentOption?.processorURL)

  return (
    processCode === PAYMETRIC_PROCESS_CODE &&
    Boolean(processorURL) &&
    !processorURL.includes('internal')
  )
}

const CARD_PAYMENT_TYPES = new Set(['creditcard', 'debitcard'])

/**
 * Returns true when the cart holds a card payment. SEPA, wire and wallet
 * payments share the GPS initiate call with cards, so the Paymetric 3DS
 * decision must check the cart payment type before it looks at the credit card
 * processor, which is always present whenever the market has one configured.
 * @param {Array} payments - cartResponse.payments
 * @returns {boolean}
 */
export const hasCardPaymentOnCart = (payments = []) =>
  Array.isArray(payments) &&
  payments.some(payment =>
    CARD_PAYMENT_TYPES.has(normalizePaymentName(payment?.type))
  )

export const resolveGpsPaymentType = (gpsOption = {}, fallbackKey = '') => {
  const id =
    normalizePaymentName(gpsOption?.id) || normalizePaymentName(fallbackKey)
  const type = normalizePaymentName(gpsOption?.type)
  const processCode = normalizePaymentName(gpsOption?.processCode)
  const processorURL = normalizePaymentName(gpsOption?.processorURL)

  const isInternal = processorURL.includes('internal')
  const isPaymetricCard =
    processCode === PAYMETRIC_PROCESS_CODE && type === GPS_CCARD_TYPE

  // An internal, non-card option is a wallet/bank transfer handled on-platform,
  // so its own id carries the type the API expects.
  if (isInternal && id && type !== GPS_CCARD_TYPE) {
    return GPS_TYPE_OVERRIDES_BY_OPTION_ID[id] || id.toUpperCase()
  }
  return isPaymetricCard ? 'CREDITCARD' : 'GPS'
}

export const isGpsRoutedOption = paymentOption =>
  isExternalGPSCreditCardOption(paymentOption) &&
  resolveGpsPaymentType(paymentOption) === 'GPS'

export const getGpsOptionId = (gpsOption = {}, fallbackKey = '') => {
  const id = (gpsOption?.id ?? '').toString().trim()
  return id || (fallbackKey ?? '').toString().trim()
}

export const getGpsRoutedCreditCardOption = (
  gpsCreditCardOptions = [],
  configuredProcessorName = ''
) => {
  if (!Array.isArray(gpsCreditCardOptions)) {
    return undefined
  }

  const normalizedConfiguredName = normalizePaymentName(configuredProcessorName)

  return normalizedConfiguredName
    ? gpsCreditCardOptions.find(
        option =>
          isGpsRoutedOption(option) &&
          normalizePaymentName(option?.name) === normalizedConfiguredName
      )
    : gpsCreditCardOptions.find(isGpsRoutedOption)
}

const getGpsCreditCardOptionForPayment = (
  gpsCreditCardOptions = [],
  configuredProcessorName = '',
  routedOption = undefined
) => {
  if (!Array.isArray(gpsCreditCardOptions)) {
    return undefined
  }

  if (routedOption) {
    return routedOption
  }

  // A configured-but-unmatched name must not fall back to another processor.
  return normalizePaymentName(configuredProcessorName)
    ? getConfiguredGPSCreditCardOption(
        gpsCreditCardOptions,
        configuredProcessorName
      )
    : gpsCreditCardOptions.find(isExternalGPSCreditCardOption)
}

/**
 * Resolves the `type` and `name` pair for a profile credit-card payment.
 * `name` carries the GPS option id rather than its displayName, and a card
 * option (type "ccard") is always named CREDITCARD so checkout recognises it.
 */
export const getGpsCreditCardPaymentIdentity = (
  gpsCreditCardOptions = [],
  configuredProcessorName = '',
  fallbackType = 'CREDITCARD'
) => {
  const routedOption = getGpsRoutedCreditCardOption(
    gpsCreditCardOptions,
    configuredProcessorName
  )
  const describingOption = getGpsCreditCardOptionForPayment(
    gpsCreditCardOptions,
    configuredProcessorName,
    routedOption
  )
  const isCreditCard =
    normalizePaymentName(describingOption?.type) === GPS_CCARD_TYPE
  const resolvedName = isCreditCard
    ? 'CREDITCARD'
    : getGpsOptionId(describingOption)

  return {
    type: routedOption ? 'GPS' : fallbackType,
    name: routedOption || isCreditCard ? resolvedName : '',
  }
}

// Payment types that should never count as a usable saved payment (e.g. in
// the checkout or profile saved payments list). Add new types here to
// exclude them the same way everywhere this is checked.
export const EXCLUDED_SAVED_PAYMENT_TYPES = [
  'PURCHASEORDER',
  'LOYALTY',
  'STORECREDIT',
  BANK_TRANSFER_TYPE,
]

export const isExcludedSavedPaymentType = (type = '') =>
  EXCLUDED_SAVED_PAYMENT_TYPES.includes((type || '').toString().toUpperCase())

export const hasNonExcludedSavedPayment = (paymentsList = []) =>
  (paymentsList || []).some(
    payment => !isExcludedSavedPaymentType(payment?.type)
  )
